import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuthContext } from "providers/Auth";
import { useCallback, useMemo, useState } from "react";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { assigneeApi } from "api/assignee";
import { toast } from "react-toastify";

import { NO_ASSIGNEE_ID } from "./constants";
import { sortAssignees, buildOptions } from "./utils";
import type { SelectedUserData } from "./types";

interface UseAssigneeFilterParams {
  open: boolean;
  selectedUserIds: number[] | null;
  onSelectedUserIdsChange: (userIds: number[] | null) => void;
  onPageChange?: (page: string) => void;
}

export const useAssigneeFilter = ({
  open,
  selectedUserIds,
  onSelectedUserIdsChange,
  onPageChange
}: UseAssigneeFilterParams) => {
  const { auth } = useAuthContext();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 500);

  const hasSelection = selectedUserIds !== null && selectedUserIds.length > 0;

  const { data: assignees, isLoading } = useQuery({
    queryKey: debouncedSearch ? ["ASSIGNEES", debouncedSearch] : ["ASSIGNEES"],
    queryFn: () =>
      assigneeApi.getAssignees(
        debouncedSearch ? { search: debouncedSearch } : undefined
      ),
    enabled: open || hasSelection
  });

  const sortedUsers = useMemo(
    () => sortAssignees(assignees, auth?.id),
    [assignees, auth]
  );

  const handleToggleUser = useCallback(
    (userId: number) => {
      const isSelected = selectedUserIds?.includes(userId) || false;
      if (isSelected) {
        const newIds = selectedUserIds?.filter((id) => id !== userId) || [];
        onSelectedUserIdsChange(newIds.length > 0 ? newIds : null);
      } else onSelectedUserIdsChange([...(selectedUserIds || []), userId]);
      onPageChange?.("1");
    },
    [selectedUserIds, onSelectedUserIdsChange, onPageChange]
  );

  const selectedUsersData = useMemo<SelectedUserData[]>(() => {
    if (!selectedUserIds || selectedUserIds.length === 0) return [];
    return selectedUserIds
      .map((id) => {
        if (id === NO_ASSIGNEE_ID)
          return {
            id,
            name: "No assignee",
            isCurrentUser: false,
            isNoAssignee: true
          };
        const isCurrentUser = id === auth?.id;
        const user = sortedUsers.find((u) => u.id === id);
        const name = user?.name || (isCurrentUser ? auth.name : null);
        if (!name) return null;
        return { id, name, isCurrentUser, isNoAssignee: false };
      })
      .filter(Boolean) as SelectedUserData[];
  }, [selectedUserIds, sortedUsers, auth]);

  const selectedCount = selectedUserIds?.length || 0;
  const isAnySelected = selectedUserIds === null;

  const displayText = useMemo(() => {
    if (isAnySelected || selectedCount === 0) return "Any";
    return selectedUsersData.map((u) => u.name).join(", ");
  }, [isAnySelected, selectedCount, selectedUsersData]);

  const { visibleSelectedUsers, extraSelectedCount } = useMemo(() => {
    const userData = selectedUsersData.filter((u) => !u.isNoAssignee);
    const visible = userData.slice(0, 3);
    return {
      visibleSelectedUsers: visible,
      extraSelectedCount: Math.max(userData.length - visible.length, 0)
    };
  }, [selectedUsersData]);

  return {
    auth,
    search,
    setSearch,
    sortedUsers,
    isLoading,
    selectedCount,
    displayText,
    visibleSelectedUsers,
    extraSelectedCount,
    handleToggleUser,
    selectedUserIds
  };
};

interface UseAssigneeSelectParams {
  value?: number | null;
  rowId?: string;
  onChange?: (partner_user_id: number | null) => void;
  onAssign?: (rowId: string, userId: number | null) => Promise<any>;
  onAssignSuccess?: () => void;
  successMessage?: string;
  errorMessage?: string;
}

export const useAssigneeSelect = ({
  value,
  rowId,
  onChange,
  onAssign,
  onAssignSuccess,
  successMessage = "Assigned successfully",
  errorMessage = "Failed to assign"
}: UseAssigneeSelectParams) => {
  const { auth } = useAuthContext();

  const { data: assignees, isLoading } = useQuery({
    queryKey: ["ASSIGNEES"],
    queryFn: () => assigneeApi.getAssignees(),
    staleTime: 5 * 60 * 1000
  });

  const { mutateAsync } = useMutation({
    mutationFn: ({
      row_id,
      partner_user_id
    }: {
      row_id: string;
      partner_user_id: number | null;
    }) => {
      if (!onAssign) return Promise.resolve();
      return onAssign(row_id, partner_user_id);
    },
    onSuccess: () => {
      onAssignSuccess?.();
      toast.success(successMessage);
    },
    onError: () => {
      toast.error(errorMessage);
    }
  });

  const sortedAssignees = useMemo(
    () => sortAssignees(assignees, auth?.id),
    [assignees, auth]
  );

  const handleAssigneeChange = useCallback(
    (userId: number | null) => {
      if (onChange) onChange(userId);
      else if (rowId && onAssign)
        mutateAsync({ row_id: String(rowId), partner_user_id: userId });
    },
    [onChange, rowId, onAssign, mutateAsync]
  );

  const options = useMemo(
    () => buildOptions(sortedAssignees),
    [sortedAssignees]
  );

  const handleChange = useCallback(
    (e: { target: { value: any } }) => {
      const nextValue = e.target.value;
      if (nextValue === "unassigned") return handleAssigneeChange(null);
      handleAssigneeChange(Number(nextValue));
    },
    [handleAssigneeChange]
  );

  return {
    auth,
    isLoading,
    options,
    value,
    handleChange
  };
};
